/**
 * Minimal, dependency-free SVG sparkline/line chart.
 *
 * Intentionally lightweight for v1 (no charting library dependency) since
 * this version carries no real market data yet; future versions with
 * live price series can swap this out or extend it without touching call
 * sites, since it only depends on a plain numeric array.
 */

import React from "react";

interface LineChartProps {
  data: number[];
  width?: number;
  height?: number;
  strokeColor?: string;
}

export default function LineChart({
  data,
  width = 320,
  height = 96,
  strokeColor = "#2FD3A6",
}: LineChartProps): React.ReactElement {
  if (data.length === 0) {
    return (
      <div
        style={{ width, height }}
        className="flex items-center justify-center text-xs text-mist-400"
      >
        No series data
      </div>
    );
  }

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const step = width / Math.max(data.length - 1, 1);

  const points = data
    .map((value, index) => {
      const x = index * step;
      const y = height - ((value - min) / range) * height;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img">
      <polyline
        points={points}
        fill="none"
        stroke={strokeColor}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
